import type { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { Window } from '../../ui/app/Window';
import { bump } from '../../ui/app/store';
import { Empty, Price, TipLayer, Wallet, useCtx } from '../economy/ui';
import {
  KNOTS,
  PORTS,
  SAILORS,
  SAIL_MIN_LEVEL,
  SEA_ZONE,
  SHIP_MAX_LEVEL,
  SHIP_NAME,
  type SailorDef,
  TOW_HULL,
  crewBonusText,
  isPort,
  shipLevelInfo,
  towFee,
  upgradeCost,
} from './data';
import { type ShipStats, canUpgrade, dismissSailor, hireSailor, lastPort, repairQuote, repairShip, shipOf, shipStats, statsOf, upgradeShip } from './ship';
import { sailGame } from './system';

/**
 * Panel `sail` (Harbormaster Sol, the menu bar's Ship button, or the helm):
 * ship status and harbour repairs, setting sail from the current port, the
 * crew roster and shipwright refits. Changes to the ship happen in port only.
 */

type Tab = 'ship' | 'crew' | 'yard';

const pct = (v: number): string => `${Math.round(v * 100)}%`;
const shipClass = (level: number): string => (level >= 10 ? 'Galleon' : level >= 7 ? 'Frigate' : level >= 4 ? 'Brigantine' : 'Sloop');

function notify(text: string, color?: string): void {
  sailGame.current?.notify(text, color);
}

/** Wallet event (HUD currencies), save soon, re-render. */
function commit(): void {
  const g = sailGame.current;
  const p = g?.progression;
  if (p) p.emit('wallet', { silver: p.account.wallet.silver, gold: p.account.wallet.gold });
  if (g) g.store.saveSoon(g.account);
  bump();
}

function ShipArt({ level, hull }: { level: number; hull: number }) {
  const masts = level >= 7 ? [38, 64, 88] : level >= 4 ? [48, 80] : [62];
  const worn = hull < 0.35;
  return (
    <svg viewBox="0 0 140 96" class={`sl-art${worn ? ' is-worn' : ''}`} aria-hidden="true">
      <defs>
        <linearGradient id="sl-sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#1b4a66" />
          <stop offset="1" stop-color="#0b1e27" />
        </linearGradient>
        <linearGradient id="sl-sail" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#efe4c8" />
          <stop offset="1" stop-color="#c9b88e" />
        </linearGradient>
      </defs>
      <rect x="0" y="70" width="140" height="26" fill="url(#sl-sea)" />
      <path d="M0 72 Q17.5 68 35 72 T70 72 T105 72 T140 72" fill="none" stroke="#6fb3d2" stroke-opacity="0.5" />
      {masts.map((x, i) => (
        <g key={x}>
          <line x1={x} y1={i === masts.length - 1 && masts.length > 1 ? 16 : 10} x2={x} y2="66" stroke="#5a4028" stroke-width="2.2" />
          <path d={`M${x - 15} ${22 + i * 2} Q${x} ${18 + i * 2} ${x + 15} ${22 + i * 2} L${x + 13} ${40 + i} Q${x} ${36 + i} ${x - 13} ${40 + i} Z`} fill="url(#sl-sail)" />
          <path d={`M${x - 17} ${44 + i} Q${x} ${40 + i} ${x + 17} ${44 + i} L${x + 15} ${60} Q${x} ${56} ${x - 15} ${60} Z`} fill="url(#sl-sail)" />
          <path d={`M${x} ${i === masts.length - 1 && masts.length > 1 ? 16 : 10} l7 3 l-7 3 Z`} fill="#c9a449" />
        </g>
      ))}
      <path d="M14 62 L126 62 L118 76 Q70 84 24 76 Z" fill="#4a3220" stroke="#2a1c10" />
      <path d="M16 64 L124 64" stroke="#c9a449" stroke-width="1.4" />
      <path d="M126 62 L136 56" stroke="#5a4028" stroke-width="2" />
    </svg>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div class="sl-stat">
      <span>{label}</span>
      <b>{value}</b>
      {note && <em>{note}</em>}
    </div>
  );
}

function statRows(s: ShipStats): { label: string; value: string }[] {
  return [
    { label: 'Top speed', value: `${(s.maxSpeed * KNOTS).toFixed(1)} kn` },
    { label: 'Acceleration', value: `${s.accel.toFixed(1)} m/s²` },
    { label: 'Turn rate', value: `${Math.round((s.turnRate * 180) / Math.PI)}°/s` },
    { label: 'Max hull', value: String(s.maxHull) },
    { label: 'Storm resistance', value: pct(s.stormResist) },
    { label: 'Crew berths', value: String(s.crewSlots) },
  ];
}

interface Where {
  port: (typeof PORTS)[string] | null;
  atSea: boolean;
}

function ShipTab({ where }: { where: Where }) {
  const ctx = useCtx();
  if (!ctx) return null;
  const { c, a } = ctx;
  const ship = shipOf(a);
  const stats = statsOf(ship);
  const q = repairQuote(ship, stats);
  const hullPct = stats.maxHull > 0 ? ship.hull / stats.maxHull : 0;
  const status = ship.hull <= 0 ? 'Wrecked — repair before sailing' : hullPct < 0.35 ? 'Badly damaged' : hullPct < 0.999 ? 'Weathered' : 'Seaworthy';
  const partial = q.missing > 0 && a.wallet.silver < q.cost ? Math.floor(a.wallet.silver / q.rate) : 0;
  const home = PORTS[lastPort(c)];
  const repair = () => {
    const r = repairShip(a);
    if (r.repaired <= 0) {
      notify('You need more silver to repair the hull.', '#e5484d');
      return;
    }
    notify(`Hull repaired (+${Math.round(r.repaired)}) for ${r.cost.toLocaleString('en-US')} silver.`, '#7bd66b');
    commit();
  };
  const sailBlock = !where.port
    ? where.atSea
      ? 'Under way'
      : 'Board at a harbour'
    : c.level < SAIL_MIN_LEVEL
      ? `Requires level ${SAIL_MIN_LEVEL}`
      : ship.hull <= 0
        ? 'Repair the hull first'
        : null;
  const setSail = () => {
    const g = sailGame.current;
    if (!g || !where.port || sailBlock) return;
    g.travel(SEA_ZONE, where.port.seaSpawn);
  };
  const extras = [
    stats.fogSight > 0 && { label: 'Sight in fog', value: `+${pct(stats.fogSight)}` },
    stats.salvageBonus > 0 && { label: 'Salvage silver', value: `+${pct(stats.salvageBonus)}` },
    stats.repairDiscount > 0 && { label: 'Repair costs', value: `−${pct(stats.repairDiscount)}` },
    stats.cannonCdr > 0 && { label: 'Cannon cooldown', value: `−${pct(stats.cannonCdr)}` },
  ].filter((x): x is { label: string; value: string } => !!x);
  return (
    <div class="sl-ship">
      <section class="sl-hero">
        <ShipArt level={ship.level} hull={hullPct} />
        <div class="sl-hero__info">
          <div class="sl-name">
            {SHIP_NAME} <span class="sl-lv">Lv {ship.level}</span>
          </div>
          <div class="sl-class">
            {shipClass(ship.level)} · {status}
          </div>
          <div class={`sl-hull${hullPct < 0.35 ? ' is-low' : ''}`}>
            <i style={{ transform: `scaleX(${Math.max(0, Math.min(1, hullPct)).toFixed(4)})` }} />
            <span>
              Hull {Math.ceil(ship.hull)} / {stats.maxHull}
            </span>
          </div>
          <div class="sl-repair">
            {q.missing > 0 ? (
              <>
                <Price cost={{ silver: q.cost }} a={a} />
                <button type="button" class="btn sm" disabled={!where.port || a.wallet.silver < q.rate} onClick={repair} title={where.port ? undefined : 'Repairs are made in port'}>
                  {partial > 0 ? `Repair ${partial}` : 'Repair'}
                </button>
                {!where.port && <span class="fx-dim">in port only</span>}
              </>
            ) : (
              <span class="fx-dim">No repairs needed.</span>
            )}
          </div>
        </div>
      </section>
      <div class="section-h">Seaworthiness</div>
      <div class="sl-stats">
        {[...statRows(stats), ...extras].map((r) => (
          <Stat key={r.label} label={r.label} value={r.value} />
        ))}
      </div>
      <div class="section-h">Voyage</div>
      <div class="sl-voyage">
        <div class="sl-voyage__text">
          {where.port ? (
            <p>
              Moored at <b>{where.port.name}</b>. The Gleaming Sea links Havenreach, Haven Isle and Glimmer Isle — sail into any pier to dock.
            </p>
          ) : where.atSea ? (
            <p>Under way on the Gleaming Sea. Dock at a harbour pier to repair, refit or change crew.</p>
          ) : (
            <p>
              {SHIP_NAME} waits at <b>{home?.name ?? 'Havenreach Harbour'}</b>. Board at a harbour (Harbormaster Sol at the Havenreach docks).
            </p>
          )}
          <p class="fx-dim">
            Storms batter the hull and fog hides derelicts. If the hull breaks, a tug tows you to {home?.name ?? 'Havenreach Harbour'} for {towFee(ship.level).toLocaleString('en-US')} silver and patches it to {pct(TOW_HULL)}.
          </p>
        </div>
        <button type="button" class="btn btn--gold sl-sail" disabled={!!sailBlock} onClick={setSail}>
          <b>Set Sail</b>
          <span>{sailBlock ?? 'The Gleaming Sea'}</span>
        </button>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2);
}

function SailorRow({ s, action }: { s: SailorDef; action: ComponentChildren }) {
  return (
    <div class="sl-sailor">
      <span class="sl-sailor__badge" style={{ background: `#${s.hue.toString(16).padStart(6, '0')}` }}>
        {initials(s.name)}
      </span>
      <div class="sl-sailor__main">
        <div class="sl-sailor__name">
          {s.name} <span>{s.role}</span>
        </div>
        <div class="sl-sailor__bonus">{crewBonusText(s)}</div>
        <div class="sl-sailor__blurb">{s.blurb}</div>
      </div>
      <div class="sl-sailor__act">{action}</div>
    </div>
  );
}

function CrewTab({ where }: { where: Where }) {
  const ctx = useCtx();
  if (!ctx) return null;
  const { a } = ctx;
  const ship = shipOf(a);
  const slots = shipLevelInfo(ship.level).crewSlots;
  let nextBerth = 0;
  for (let l = ship.level + 1; l <= SHIP_MAX_LEVEL && !nextBerth; l++) if (shipLevelInfo(l).crewSlots > slots) nextBerth = l;
  const aboard = ship.crew.map((id) => SAILORS.find((s) => s.id === id)).filter((s): s is SailorDef => !!s);
  const ashore = SAILORS.filter((s) => !ship.crew.includes(s.id));
  const full = ship.crew.length >= slots;
  const hire = (s: SailorDef) => {
    const r = hireSailor(a, s.id);
    if (r === 'ok') {
      notify(`${s.name} signs on as ${s.role}.`, '#7ad8ff');
      commit();
    } else notify(r === 'full' ? 'Every berth is taken.' : r === 'silver' ? `You need ${s.cost} silver.` : 'They are already aboard.', '#e5484d');
  };
  const dismiss = (s: SailorDef) => {
    if (dismissSailor(a, s.id)) {
      notify(`${s.name} goes ashore.`);
      commit();
    }
  };
  return (
    <div class="sl-crew">
      <div class="sl-berths">
        {Array.from({ length: slots }, (_, i) => (
          <span key={i} class={`sl-berth${i < ship.crew.length ? ' is-full' : ''}`} />
        ))}
        <span>
          <b class="fx-num">
            {ship.crew.length}/{slots}
          </b>{' '}
          berths{nextBerth ? ` · another at ship level ${nextBerth}` : ''}
        </span>
        {!where.port && <span class="fx-dim sl-berths__note">Crew changes are made in port.</span>}
      </div>
      <div class="section-h">Aboard</div>
      {aboard.length === 0 ? (
        <Empty title="No crew signed on">Hire sailors from the harbour below — each brings a small bonus.</Empty>
      ) : (
        aboard.map((s) => (
          <SailorRow
            key={s.id}
            s={s}
            action={
              <button type="button" class="btn sm" disabled={!where.port} onClick={() => dismiss(s)}>
                Dismiss
              </button>
            }
          />
        ))
      )}
      <div class="section-h">Looking for a berth</div>
      {ashore.map((s) => (
        <SailorRow
          key={s.id}
          s={s}
          action={
            <>
              <Price cost={{ silver: s.cost }} a={a} />
              <button type="button" class="btn sm" disabled={!where.port || full || a.wallet.silver < s.cost} onClick={() => hire(s)}>
                Hire
              </button>
            </>
          }
        />
      ))}
    </div>
  );
}

function YardTab({ where }: { where: Where }) {
  const ctx = useCtx();
  if (!ctx) return null;
  const { c, a } = ctx;
  const ship = shipOf(a);
  const cur = statsOf(ship);
  const cost = upgradeCost(ship.level);
  if (!cost) return <Empty title={`${SHIP_NAME} is fully refitted`}>No shipwright on the isles can improve her further.</Empty>;
  const next = shipStats(ship.level + 1, ship.crew);
  const rows = statRows(cur).map((r, i) => ({ ...r, next: statRows(next)[i]?.value ?? r.value }));
  const refit = () => {
    if (!upgradeShip(c, a)) {
      notify('The shipwright needs more silver or materials.', '#e5484d');
      return;
    }
    notify(`${SHIP_NAME} refitted to level ${ship.level}!`, '#ffd070');
    commit();
  };
  return (
    <div class="sl-yard">
      <div class="sl-yard__head">
        <span class="sl-lv">Lv {ship.level}</span>
        <span class="sl-arrow">→</span>
        <span class="sl-lv is-next">Lv {ship.level + 1}</span>
        <span class="fx-dim">
          {shipClass(ship.level)}
          {shipClass(ship.level + 1) !== shipClass(ship.level) ? ` → ${shipClass(ship.level + 1)}` : ''}
        </span>
      </div>
      <div class="sl-compare">
        {rows.map((r) => (
          <div key={r.label} class={`sl-compare__row${r.next !== r.value ? ' is-up' : ''}`}>
            <span>{r.label}</span>
            <b>{r.value}</b>
            <i>→</i>
            <b>{r.next}</b>
          </div>
        ))}
      </div>
      <div class="section-h">Refit cost</div>
      <div class="sl-cost">
        <Price cost={cost} c={c} a={a} />
        <button type="button" class="btn btn--gold" disabled={!where.port || !canUpgrade(c, a)} onClick={refit}>
          Refit to Lv {ship.level + 1}
        </button>
      </div>
      <p class="fx-dim sl-yard__note">
        {where.port ? '' : 'Refits are made in port. '}Timber and iron ore come from logging and mining (and flotsam at sea); heartwood, relic fragments and starsteel are salvaged from derelicts in storms and fog banks.
      </p>
    </div>
  );
}

export function SailPanel() {
  const ctx = useCtx();
  const [tab, setTab] = useState<Tab>('ship');
  if (!ctx) return null;
  const { c, a } = ctx;
  const g = sailGame.current;
  const zoneId = g?.zoneId ?? null;
  const where: Where = { port: isPort(zoneId) ? (PORTS[zoneId] ?? null) : null, atSea: g?.zoneKind === 'sea' };
  const tabs: [Tab, string][] = [
    ['ship', 'Ship'],
    ['crew', 'Crew'],
    ['yard', 'Shipwright'],
  ];
  return (
    <Window title={where.port ? `Harbour — ${where.port.name}` : SHIP_NAME} width="min(700px, 96vw)" className="fx-win sl-win">
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" class="tab" aria-selected={tab === id} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
        <span class="fx-spacer" />
        <Wallet a={a} c={c} tokens={['timber', 'ore_raw']} />
      </div>
      <div class="fx-rule" />
      <div class="fx-scroll sl-body">
        {tab === 'ship' && <ShipTab where={where} />}
        {tab === 'crew' && <CrewTab where={where} />}
        {tab === 'yard' && <YardTab where={where} />}
      </div>
      <TipLayer />
    </Window>
  );
}
